import type { AxiosInstance } from "axios";
import { Check, Copy, Link2, Loader2, Send } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { apiErrorMessage } from "../lib/api.js";
import {
  createStoreInvite,
  fetchStoreInvites,
  formatInviteDate,
  INVITE_STATUS_LABEL,
  revokeStoreInvite,
  type CreatedInvite,
  type StoreInvite,
  type StoreInviteStatus,
  type StoreInvites,
} from "../lib/store-invites.js";
import { cn } from "../lib/cn.js";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card.js";
import { Input } from "./ui/input.js";
import { Label } from "./ui/label.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table.js";

/**
 * INVITE A CLIENT (Flow 2): an agency creates a one-time link, sends it to a client, and the
 * client's own Shopify install lands in the agency as that client's workspace — instead of the
 * separate account a client's App Store install would make.
 *
 * Shown only to an agency's owner or admin (the API says `available: false` to everyone else, and
 * refuses them regardless). The link is shown ONCE: only a hash of it is kept.
 */
const STATUS_TONE: Record<StoreInviteStatus, string> = {
  active: "border-blue-300 text-blue-700 dark:text-blue-300",
  used: "border-emerald-400 text-emerald-700 dark:text-emerald-400",
  expired: "text-muted-foreground",
  revoked: "text-muted-foreground",
};

export function ClientInvites({ client }: { readonly client: AxiosInstance }): React.JSX.Element | null {
  const [data, setData] = useState<StoreInvites | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<(CreatedInvite & { readonly clientName: string }) | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await fetchStoreInvites(client));
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load your client invites."));
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  if (data === null || !data.available) return null;

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const clientName = name.trim();
      setCreated({ ...(await createStoreInvite(client, clientName)), clientName });
      setName("");
      await load();
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not create the link."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card className="gap-5">
        <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Send className="text-primary size-5" />
              Invite a client to connect their store
            </CardTitle>
            <CardDescription>
              Create a link for a client. When they install Ecomally on their Shopify store from it, the store joins{" "}
              {data.orgName ?? "your agency"} as that client — no separate account for them.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="grid min-w-56 flex-1 gap-1.5">
                <Label htmlFor="invite-client-name">Client name</Label>
                <Input
                  id="invite-client-name"
                  placeholder="For example: Masakali"
                  maxLength={100}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={busy}
                />
              </div>
              <Button type="submit" disabled={busy || name.trim() === ""}>
                {busy ? <Loader2 className="animate-spin" /> : <Link2 />}
                Create link
              </Button>
            </div>

            {created !== null && <NewLink invite={created} />}
            {error !== null && (
              <p className="text-destructive text-sm" role="alert">
                {error}
              </p>
            )}
          </CardContent>
        </form>
      </Card>

      {data.invites.length > 0 && (
        <Card className="gap-4">
          <CardHeader>
            <CardTitle className="text-base">Client invites</CardTitle>
            <CardDescription>Each link connects one store, and stops working after 7 days.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-auto rounded-md border">
              <Table className="min-w-[640px]">
                <TableHeader className="bg-card sticky top-0">
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead className="w-40">Status</TableHead>
                    <TableHead>Store</TableHead>
                    <TableHead className="w-32">Expires</TableHead>
                    <TableHead className="w-36" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.invites.map((invite) => (
                    <InviteRow key={invite.id} invite={invite} client={client} onChanged={() => void load()} />
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

/** The link, shown the one time it exists, with a copy button. */
function NewLink({ invite }: { readonly invite: CreatedInvite & { readonly clientName: string } }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(invite.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch (error) {
      // No clipboard (an insecure context, a refusing browser): the link is selectable in the box.
      void error;
    }
  };
  return (
    <div data-testid="new-invite-link" className="flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-3 dark:border-blue-900 dark:bg-blue-950/30">
      <p className="text-sm">
        Send this link to <span className="font-medium">{invite.clientName}</span>. It works once, until{" "}
        {formatInviteDate(invite.expiresAt)} — copy it now, it will not be shown again.
      </p>
      <div className="flex gap-2">
        <Input readOnly value={invite.url} aria-label="Invite link" className="bg-background font-mono text-xs" onFocus={(e) => e.target.select()} />
        <Button type="button" variant="outline" onClick={() => void copy()} className="shrink-0">
          {copied ? <Check /> : <Copy />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
    </div>
  );
}

function InviteRow({
  invite,
  client,
  onChanged,
}: {
  readonly invite: StoreInvite;
  readonly client: AxiosInstance;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revoke = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await revokeStoreInvite(client, invite.id);
      onChanged();
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not revoke the link."));
      setBusy(false);
    }
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{invite.clientName}</TableCell>
      <TableCell>
        <Badge variant="outline" className={cn(STATUS_TONE[invite.status])}>
          {INVITE_STATUS_LABEL[invite.status]}
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground">{invite.store?.shopDomain ?? "—"}</TableCell>
      <TableCell className="text-muted-foreground whitespace-nowrap">
        {invite.status === "used" && invite.usedAt !== null ? `Used ${formatInviteDate(invite.usedAt)}` : formatInviteDate(invite.expiresAt)}
      </TableCell>
      <TableCell className="text-right">
        {invite.status === "active" &&
          (confirming ? (
            <span className="inline-flex items-center gap-1">
              <Button size="sm" variant="destructive" disabled={busy} onClick={() => void revoke()}>
                {busy && <Loader2 className="animate-spin" />}
                Revoke
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirming(false)}>
                Keep
              </Button>
            </span>
          ) : (
            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setConfirming(true)}>
              Revoke…
            </Button>
          ))}
        {error !== null && (
          <p className="text-destructive text-xs" role="alert">
            {error}
          </p>
        )}
      </TableCell>
    </TableRow>
  );
}
